// @flow
import { get } from 'lodash';
import {
  formatAmountForLocale,
  type CurrencyOptions,
} from 'Utils/currency-config-for-locale/currencyConfigForLocale';
import { getBookingPrecision } from '@clubmed/domains-service';
import { t, getKeyValue } from 'Src/i18n';

const MAX_LENGTH = 10;

function getCurrencySymbol(locale: string, currency: string) {
  return (0)
    .toLocaleString(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    .replace(/\d/g, '')
    .trim();
}

function hasPrice(price: *): boolean {
  return !!get(price, 'value');
}

function hasAtLeastOnePrice(props: * = {}): boolean {
  const { initialPrice, bestPrice } = props || {};
  return hasPrice(initialPrice) || hasPrice(bestPrice);
}

function isLongFormat(amount: number): boolean {
  return amount ? amount.toString().length > MAX_LENGTH : false;
}

const getTotalPriceWithDonation = (
  price: ProposalPrice,
  locale: string,
): ProposalPriceWithDonation => {
  const { donation, total, totalWithoutDiscount } = price;

  const totalWithDonation = get(donation, 'value', 0) + get(total, 'value', 0);
  const totalWithoutDiscountWithDonation =
    get(donation, 'value', 0) + get(totalWithoutDiscount, 'value', 0);
  const currencyOptions: CurrencyOptions = { precision: getBookingPrecision(locale) };

  return {
    ...price,
    withDonation: {
      total: {
        value: totalWithDonation,
        formattedValue: formatAmountForLocale(
          totalWithDonation,
          locale,
          price.currency,
          currencyOptions,
        ),
      },
      totalWithoutDiscount: {
        value: totalWithoutDiscountWithDonation,
        formattedValue: formatAmountForLocale(
          totalWithoutDiscountWithDonation,
          locale,
          price.currency,
          currencyOptions,
        ),
      },
      hasFlexRates: price.hasFlexRates,
      selectedRate: price.selectedRate,
    },
  };
};

export const getInstalmentPrice = (price?: FormattedPrice, locale: string) => {
  if (!price || !price.value) return '';
  if (getKeyValue('common.price.displayInstalmentsPrice')) {
    const instalmentValue = getKeyValue('common.price.instalmentValue');
    return t('common.price.instalmentPrice', {
      instalmentValue,
      dividedPrice: formatAmountForLocale(
        Math.ceil(price.value / instalmentValue),
        locale,
        price.currency,
        {
          precision: 0,
        },
      ),
    });
  }
  return price.formattedValue;
};

export { getCurrencySymbol, hasAtLeastOnePrice, hasPrice, isLongFormat, getTotalPriceWithDonation };
